/**
 * AddReminderModal Component
 * Modal for creating a growth reminder with AI image generation.
 * Same UI as AiImageGenerator modal.
 */

import { useQuery } from "@tanstack/react-query"
import { Loader2, Sparkles } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { AIGeneratedIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useGenerateImage } from "@/features/media/hooks/useGenerateImage"
import type { GeneratedImage } from "@/features/media/services/ai-image.service"
import { getRecentImages, markImageUsed } from "@/features/media/services/ai-image.service"
import { useTranslation } from "@/language"
import { useGrowthReminders } from "../hooks/useGrowthReminders"

const MAX_TEXT_LENGTH = 80

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

async function base64ToFile(base64: string, mimeType: string, index: number): Promise<File> {
  const res = await fetch(`data:${mimeType};base64,${base64}`)
  const blob = await res.blob()
  const extension = MIME_TO_EXT[mimeType] || "png"
  return new File([blob], `reminder-${Date.now()}-${index}.${extension}`, { type: mimeType })
}

interface AddReminderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultText?: string
}

export function AddReminderModal({ open, onOpenChange, defaultText = "" }: AddReminderModalProps) {
  const { t } = useTranslation("myRituality")
  const { createReminder, isCreating } = useGrowthReminders()
  const { mutateAsync, error } = useGenerateImage()

  const [text, setText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [selectedRecentId, setSelectedRecentId] = useState<string | null>(null)
  const [hasGenerated, setHasGenerated] = useState(false)

  const { data: recentImages = [] } = useQuery({
    queryKey: ["ai-generated-images", "recent"],
    queryFn: () => getRecentImages(5),
    enabled: open,
    staleTime: 0,
  })

  useEffect(() => {
    if (open) {
      setText(defaultText)
      setIsLoading(false)
      setGeneratedImages([])
      setGenerationId(null)
      setSelectedIndex(null)
      setSelectedRecentId(null)
      setHasGenerated(false)
    }
  }, [open, defaultText])

  const handleGenerate = async () => {
    if (!text.trim()) return

    setIsLoading(true)
    setSelectedIndex(null)
    setSelectedRecentId(null)

    try {
      const result = await mutateAsync({ entityType: "growth-reminder", prompt: text.trim() })
      setGeneratedImages(result.images)
      setGenerationId(result.generationId)
      setHasGenerated(true)
    } catch {
      // Error handled by hook
    } finally {
      setIsLoading(false)
    }
  }

  const handleUseImage = async () => {
    if (selectedIndex === null || !generatedImages[selectedIndex] || !generationId) return
    if (selectedRecentId) return

    const img = generatedImages[selectedIndex]
    const file = await base64ToFile(img.base64, img.mimeType, selectedIndex)
    await markImageUsed(generationId, selectedIndex)
    createReminder(
      { text: text.trim(), imageFile: file },
      {
        onSuccess: () => {
          toast.success(t("reminders.modal.saved"))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t("reminders.modal.saveFailed"))
        },
      },
    )
  }

  const handleUseRecent = async () => {
    if (!selectedRecentId) return

    const recentImg = recentImages.find(img => img.id === selectedRecentId)
    if (!recentImg) return

    const file = await base64ToFile(recentImg.image, "image/png", recentImg.indexNumber)
    await markImageUsed(recentImg.generationId, recentImg.indexNumber)
    createReminder(
      { text: text.trim(), imageFile: file },
      {
        onSuccess: () => {
          toast.success(t("reminders.modal.saved"))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t("reminders.modal.saveFailed"))
        },
      },
    )
  }

  const isUseDisabled = selectedIndex === null && selectedRecentId === null

  const errorMessage = error ? (error as { message?: string })?.message || error.message : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-4"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="pt-5 px-3.5">
          <DialogTitle className="text-[24px] font-semibold uppercase">
            {t("reminders.modal.title")}
          </DialogTitle>
          <DialogDescription className="text-[14px] text-muted-foreground leading-[25px]">
            {t("reminders.modal.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Text Input Section */}
          <div className="space-y-2 px-3">
            <label className="text-[12px] font-medium uppercase leading-[12px]">
              {t("reminders.modal.inputLabel")} *
            </label>
            <input
              type="text"
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_TEXT_LENGTH))}
              onKeyDown={e => {
                if (e.key === "Enter" && !hasGenerated && text.trim().length >= 3) {
                  e.preventDefault()
                  handleGenerate()
                }
              }}
              placeholder={t("reminders.modal.inputPlaceholder")}
              disabled={isLoading || isCreating}
              className="w-full min-h-[35px] rounded-none border-b border-border bg-transparent py-1 text-[12px] text-primary placeholder:text-muted-foreground focus:outline-none resize-none leading-[20px]"
            />
            <p className="text-xs text-muted-foreground">
              {text.length}/{MAX_TEXT_LENGTH}
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          {/* Image Grid */}
          {isLoading ? (
            <div className="px-2">
              <div className="h-[302.5px] bg-base rounded-tl-2xl rounded-br-2xl flex items-center justify-center">
                <div className="border border-white p-4 rounded-full">
                  <AIGeneratedIcon className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>
            </div>
          ) : generatedImages.length > 0 ? (
            <div className="px-2 overflow-y-auto" style={{ maxHeight: "302.5px" }}>
              <div className="flex flex-col gap-3">
                {generatedImages.map((img, i) => (
                  <button
                    key={`gen-${i}`}
                    type="button"
                    onClick={() => {
                      setSelectedIndex(i)
                      setSelectedRecentId(null)
                    }}
                    className={`relative h-[302.5px] rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none w-full ${
                      selectedIndex === i
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:${img.mimeType};base64,${img.base64}`}
                      alt={`Generated option ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <p className="z-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-[24px] font-semibold text-center py-1 px-2 rounded">
                      {text}
                    </p>
                    {selectedIndex === i && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-primary-foreground"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!hasGenerated ? (
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !text.trim() || text.trim().length < 3}
                className="flex-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t("reminders.modal.generateImage")}
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="flex-1"
                >
                  <Loader2 className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
                  {t("reminders.modal.regenerate")}
                </Button>
                <Button
                  onClick={selectedRecentId ? handleUseRecent : handleUseImage}
                  disabled={isUseDisabled || isLoading || isCreating}
                  className="flex-1"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("reminders.modal.saving")}
                    </>
                  ) : (
                    t("reminders.modal.use")
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Recent Generations */}
          {recentImages.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">recent generations</p>
              <Carousel
                data={recentImages}
                renderItem={img => (
                  <button
                    type="button"
                    onClick={async () => {
                      setSelectedRecentId(img.id)
                      setSelectedIndex(null)
                      const file = await base64ToFile(img.image, "image/png", img.indexNumber)
                      await markImageUsed(img.generationId, img.indexNumber)
                      createReminder(
                        { text: text.trim(), imageFile: file },
                        {
                          onSuccess: () => {
                            toast.success(t("reminders.modal.saved"))
                            onOpenChange(false)
                          },
                          onError: () => {
                            toast.error(t("reminders.modal.saveFailed"))
                          },
                        },
                      )
                    }}
                    className={`relative w-22 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none ${
                      selectedRecentId === img.id
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:image/png;base64,${img.image}`}
                      alt="Recent generated"
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                itemWidth="88px"
                contentClassName="gap-2 touch-pan-x"
                opts={{ dragFree: true, loop: false, containScroll: "trimSnaps" }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
